import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { PencilIcon, SortIcon } from '@/utils/icons';
import type { IAdminProfession } from '@apis/adminProfessions';
import { TradePill } from '@components/ui/TradePill/TradePill';
import { humanizeCamelCase, tradeLabel } from '@utils/tradeLabel';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import table from '../../adminTable.module.css';
import styles from './ProfessionsTable.module.css';

type SortKey = 'active' | 'tradeGroup' | 'trade' | 'specialty' | 'contractor';
type SortDir = 'asc' | 'desc';

// Column order: active toggle first, identity
// columns, then the rendered profession chip ("display") and the row edit.
const SORTABLE_COLUMNS: { key: SortKey; labelKey: string }[] = [
  { key: 'active', labelKey: 'adminProfessions.col.active' },
  { key: 'tradeGroup', labelKey: 'adminProfessions.col.tradeGroup' },
  { key: 'trade', labelKey: 'adminProfessions.col.trade' },
  { key: 'specialty', labelKey: 'adminProfessions.col.specialty' },
  { key: 'contractor', labelKey: 'adminProfessions.col.contractor' },
];

function groupLabel(profession: IAdminProfession, t: TFunction): string {
  return profession.tradeGroup
    ? t(`trades.${profession.tradeGroup}`, { defaultValue: humanizeCamelCase(profession.tradeGroup) })
    : '';
}

/** Default ordering: sortIndex, then group / trade labels, then
 *  specialty and contractor (`tradeGroupSortFunctions`). */
function defaultCompare(a: IAdminProfession, b: IAdminProfession, t: TFunction): number {
  return (
    (a.sortIndex ?? 0) - (b.sortIndex ?? 0) ||
    groupLabel(a, t).localeCompare(groupLabel(b, t)) ||
    tradeLabel(t, a.tradeId).localeCompare(tradeLabel(t, b.tradeId)) ||
    (a.specialty ?? '').localeCompare(b.specialty ?? '') ||
    (a.contractor ?? '').localeCompare(b.contractor ?? '')
  );
}

function compare(a: IAdminProfession, b: IAdminProfession, key: SortKey, t: TFunction): number {
  switch (key) {
    case 'active':
      return Number(a.isActive) - Number(b.isActive) || defaultCompare(a, b, t);
    case 'tradeGroup':
      return defaultCompare(a, b, t);
    case 'trade':
      // The trade column sorts by tradeId, not the localized label.
      return a.tradeId.localeCompare(b.tradeId);
    case 'specialty':
      return (a.specialty ?? '').localeCompare(b.specialty ?? '');
    case 'contractor':
      return (a.contractor ?? '').localeCompare(b.contractor ?? '');
    default:
      return 0;
  }
}

interface IProfessionsTableProps {
  professions: IAdminProfession[];
  emptyMessage: string;
  /** Profession id with an in-flight toggle mutation — its switch is disabled. */
  togglingId: string | null;
  onToggleActive: (profession: IAdminProfession, active: boolean) => void;
  onEdit: (profession: IAdminProfession) => void;
}

export function ProfessionsTable({
  professions,
  emptyMessage,
  togglingId,
  onToggleActive,
  onEdit,
}: IProfessionsTableProps) {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState<SortKey>('active');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  const sorted = useMemo(() => {
    const copy = [...professions];
    copy.sort((a, b) => {
      const c = compare(a, b, sortKey, t);
      return sortDir === 'asc' ? c : -c;
    });
    return copy;
  }, [professions, sortKey, sortDir, t]);

  return (
    <ScrollArea orientation="both" className={table.tableWrap}>
      <table className={table.table}>
        <thead>
          <tr className={table.headerRow}>
            {SORTABLE_COLUMNS.map((col) => (
              <th key={col.key} className={table.th}>
                <button type="button" className={table.sortBtn} onClick={() => handleSort(col.key)}>
                  {t(col.labelKey)}
                  <SortIcon />
                </button>
              </th>
            ))}
            <th className={table.th}>{t('adminProfessions.col.display')}</th>
            <th className={`${table.th} ${table.thCenter}`}>{t('adminProfessions.col.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((profession) => (
            <tr key={profession._id} className={table.row}>
              <td className={table.td}>
                <button
                  type="button"
                  role="switch"
                  aria-checked={profession.isActive}
                  aria-label={t(
                    profession.isActive
                      ? 'adminProfessions.toggle.deactivate'
                      : 'adminProfessions.toggle.activate',
                  )}
                  className={
                    profession.isActive ? `${styles.toggle} ${styles.toggleOn}` : styles.toggle
                  }
                  disabled={togglingId === profession._id}
                  onClick={() => onToggleActive(profession, !profession.isActive)}
                >
                  <span className={styles.toggleKnob} />
                </button>
              </td>
              <td className={table.td}>{groupLabel(profession, t) || '—'}</td>
              <td className={table.td}>{tradeLabel(t, profession.tradeId)}</td>
              <td className={table.td}>
                {profession.specialty || <span className={table.muted}>—</span>}
              </td>
              <td className={table.td}>
                {profession.contractor || <span className={table.muted}>—</span>}
              </td>
              <td className={table.td}>
                <TradePill
                  label={tradeLabel(t, profession.tradeId)}
                  backgroundColor={profession.backgroundColor || undefined}
                  tradeId={profession.tradeId}
                />
              </td>
              <td className={`${table.td} ${table.tdCenter}`}>
                <button
                  type="button"
                  className={styles.editBtn}
                  aria-label={t('adminProfessions.edit.title')}
                  onClick={() => onEdit(profession)}
                >
                  <PencilIcon size={15} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {sorted.length === 0 && <div className={table.empty}>{emptyMessage}</div>}
    </ScrollArea>
  );
}
